import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  LucideCamera,
  LucideCheck,
  LucideRefreshCw,
  LucideTrash,
  LucideUserCircle,
} from '@lucide/angular';
import { FieldBase } from '../shared/field-base';
import { FieldErrorComponent } from '../shared/field-error.component';
import { ModalComponent } from '../modal/modal.component';

type CaptureState = 'idle' | 'streaming' | 'reviewing' | 'denied' | 'unsupported';

@Component({
  selector: 'ds-camera-capture',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FieldErrorComponent, NgComponentOutlet, ModalComponent],
  template: `
    <div class="ds-field capture-field">
      <span class="ds-field__label" [id]="fieldId + '-label'">
        {{ label() }}
        @if (required()) {
          <span class="ds-field__required" aria-hidden="true">*</span>
        }
      </span>
      @if (helperText()) {
        <p class="ds-field__helper" [id]="fieldId + '-helper'">{{ helperText() }}</p>
      }

      @switch (inlineState()) {
        @case ('empty') {
          <div class="zone zone--idle">
            <span class="zone__icon" aria-hidden="true">
              <ng-container
                [ngComponentOutlet]="userIcon"
                [ngComponentOutletInputs]="{ size: 48, strokeWidth: 1.5 }"
              />
            </span>
            <p class="zone__hint">Ambil foto langsung dari kamera perangkat Anda.</p>
            <button
              type="button"
              class="zone__btn"
              [attr.aria-describedby]="fieldId + '-label ' + (describedBy() ?? '')"
              (click)="startCamera()"
              (blur)="onTouched()"
            >
              <ng-container
                [ngComponentOutlet]="cameraIcon"
                [ngComponentOutletInputs]="{ size: 20, strokeWidth: 2 }"
              />
              Buka Kamera
            </button>
            <p class="zone__meta">Pastikan wajah terlihat jelas dan pencahayaan cukup.</p>
          </div>
        }

        @case ('hasPhoto') {
          <div class="preview">
            <img #photo class="preview__photo" [src]="previewUrl()" alt="Foto selfie yang diambil" />
            <div class="preview__actions preview__actions--photo">
              <button type="button" class="retake-btn" (click)="startCamera()">
                <ng-container
                  [ngComponentOutlet]="retakeIcon"
                  [ngComponentOutletInputs]="{ size: 18, strokeWidth: 2 }"
                />
                Ambil Ulang
              </button>
              <button type="button" class="remove-btn" (click)="removePhoto()">
                <ng-container
                  [ngComponentOutlet]="trashIcon"
                  [ngComponentOutletInputs]="{ size: 18, strokeWidth: 2 }"
                />
                Hapus
              </button>
            </div>
          </div>
        }
      }

      @if (shownError(); as message) {
        <ds-field-error [errorId]="fieldId + '-error'">{{ message }}</ds-field-error>
      }
    </div>

    <ds-modal
      [open]="modalOpen()"
      title="Ambil Foto Selfie"
      size="sm"
      [dismissible]="state() !== 'streaming'"
      (closed)="closeCamera()"
    >
      @switch (state()) {
        @case ('streaming') {
          <div class="cam-preview">
            <video
              #video
              class="cam-preview__video"
              autoplay
              playsinline
              muted
              [attr.aria-label]="'Pratinjau kamera untuk ' + label()"
            ></video>
            <div class="cam-preview__actions">
              <button type="button" class="capture-btn" (click)="capture()" aria-label="Ambil foto">
                <span class="capture-btn__ring"></span>
              </button>
            </div>
            <p class="cam-preview__hint" aria-live="polite">
              Posisikan wajah di lingkaran, lalu tekan tombol untuk mengambil foto.
            </p>
          </div>
        }

        @case ('reviewing') {
          <div class="cam-review">
            <img #reviewPhoto class="cam-review__photo" [src]="pendingPreviewUrl()" alt="Pratinjau foto selfie" />
            <p class="cam-review__hint">Periksa foto Anda. Jika sudah sesuai, klik "Gunakan Foto".</p>
          </div>
        }

        @case ('denied') {
          <div class="zone zone--error">
            <p class="zone__error-text">Akses kamera ditolak.</p>
            <p class="zone__hint">
              Izinkan akses kamera di pengaturan browser, atau gunakan pilih file dari perangkat.
            </p>
            <div class="zone__btn-group">
              <button type="button" class="zone__btn" (click)="startCamera()">Coba Lagi</button>
              <label class="zone__btn zone__btn--secondary">
                Pilih dari File
                <input
                  type="file"
                  accept="image/*"
                  capture="user"
                  class="zone__native"
                  aria-hidden="true"
                  tabindex="-1"
                  (change)="onFileSelected($event)"
                />
              </label>
            </div>
          </div>
        }

        @case ('unsupported') {
          <div class="zone zone--error">
            <p class="zone__error-text">Kamera tidak tersedia.</p>
            <p class="zone__hint">
              Perangkat Anda tidak mendukung akses kamera. Silakan pilih foto dari perangkat.
            </p>
            <label class="zone__btn">
              Pilih dari File
              <input
                type="file"
                accept="image/*"
                capture="user"
                class="zone__native"
                aria-hidden="true"
                tabindex="-1"
                (change)="onFileSelected($event)"
              />
            </label>
          </div>
        }
      }

      <div ds-modal-footer>
        @if (state() === 'reviewing') {
          <div class="footer-actions">
            <button type="button" class="btn btn--secondary" (click)="retakeInModal()">
              <ng-container
                [ngComponentOutlet]="retakeIcon"
                [ngComponentOutletInputs]="{ size: 18, strokeWidth: 2 }"
              />
              Ambil Ulang
            </button>
            <button type="button" class="btn btn--primary" (click)="confirmPhoto()">
              <ng-container
                [ngComponentOutlet]="checkIcon"
                [ngComponentOutletInputs]="{ size: 18, strokeWidth: 2 }"
              />
              Gunakan Foto
            </button>
          </div>
        }
      </div>
    </ds-modal>
  `,
  styles: `
    // .capture-field {
    //   max-width: 420px;
    // }

    .zone {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-2);
      padding: var(--space-6) var(--space-5);
      background: var(--color-surface);
      border: 2px dashed var(--color-border-input);
      border-radius: var(--radius-lg);
      text-align: center;
    }

    .zone--error {
      border-color: var(--color-error);
      background: var(--color-error-tint, var(--color-surface));
    }

    .zone__icon {
      color: var(--color-teal-400);
    }

    .zone__hint {
      margin: 0;
      color: var(--color-text-secondary);
      font-size: var(--text-sm);
      max-width: 280px;
    }

    .zone__error-text {
      margin: 0;
      font-weight: var(--font-weight-bold);
      color: var(--color-error);
    }

    .zone__meta {
      margin: 0;
      font-size: var(--text-sm);
      color: var(--color-text-secondary);
    }

    .zone__btn {
      display: inline-flex;
      align-items: center;
      gap: var(--space-2);
      min-height: var(--touch-target-comfort);
      padding: 0 var(--space-5);
      border: 2px solid var(--color-primary);
      border-radius: var(--radius-full);
      background: var(--color-surface);
      color: var(--color-primary);
      font-size: var(--text-md);
      font-weight: var(--font-weight-bold);
      cursor: pointer;
      transition: background-color var(--motion-fast);
      position: relative;

      &:hover {
        background: var(--color-primary-tint);
      }
    }

    .zone__btn--secondary {
      border-color: var(--color-border);
      color: var(--color-text-secondary);
    }

    .zone__btn-group {
      display: flex;
      gap: var(--space-3);
      flex-wrap: wrap;
      justify-content: center;
    }

    .zone__native {
      position: absolute;
      inset: 0;
      opacity: 0;
      cursor: pointer;
    }

    .preview {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-3);
    }

    .preview__photo {
      width: 100%;
      max-width: 360px;
      aspect-ratio: 3 / 4;
      object-fit: cover;
      border-radius: var(--radius-md);
      background: #000;
      border: 2px solid var(--color-border-input);
    }

    .preview__actions {
      display: flex;
      align-items: center;
      gap: var(--space-4);
    }

    .preview__actions--photo {
      flex-wrap: wrap;
      justify-content: center;
    }

    .cam-preview {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-3);
    }

    .cam-preview__video {
      width: 100%;
      max-width: 360px;
      aspect-ratio: 3 / 4;
      object-fit: cover;
      border-radius: var(--radius-md);
      background: #000;
      border: 2px solid var(--color-border-input);
      transform: scaleX(-1);
    }

    .cam-preview__actions {
      display: flex;
      align-items: center;
      gap: var(--space-4);
    }

    .cam-preview__hint {
      margin: 0;
      font-size: var(--text-sm);
      color: var(--color-text-secondary);
      text-align: center;
      max-width: 300px;
    }

    .cam-review {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-3);
    }

    .cam-review__photo {
      width: 100%;
      max-width: 360px;
      aspect-ratio: 3 / 4;
      object-fit: cover;
      border-radius: var(--radius-md);
      background: #000;
      border: 2px solid var(--color-border-input);
    }

    .cam-review__hint {
      margin: 0;
      font-size: var(--text-sm);
      color: var(--color-text-secondary);
      text-align: center;
    }

    .capture-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 64px;
      height: 64px;
      border: 3px solid var(--color-primary);
      border-radius: var(--radius-full);
      background: var(--color-surface);
      cursor: pointer;
      padding: 0;
      transition: background var(--motion-fast);

      &:hover .capture-btn__ring {
        background: var(--color-primary-tint);
      }

      &:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
      }
    }

    .capture-btn__ring {
      width: 48px;
      height: 48px;
      border-radius: var(--radius-full);
      background: var(--color-primary);
      transition: background var(--motion-fast);
    }

    .retake-btn,
    .remove-btn {
      display: inline-flex;
      align-items: center;
      gap: var(--space-1);
      min-height: var(--touch-target);
      padding: 0 var(--space-4);
      border: 2px solid var(--color-border-input);
      border-radius: var(--radius-full);
      background: var(--color-surface);
      color: var(--color-text);
      font-size: var(--text-sm);
      font-weight: var(--font-weight-bold);
      cursor: pointer;
      transition: background var(--motion-fast);

      &:hover {
        background: var(--color-bg-subtle);
      }

      &:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
      }
    }

    .remove-btn {
      border-color: var(--color-error);
      color: var(--color-error-strong);

      &:hover {
        background: var(--color-error-tint);
      }
    }

    .footer-actions {
      display: flex;
      gap: var(--space-3);
      justify-content: center;
    }

    .btn {
      display: inline-flex;
      align-items: center;
      gap: var(--space-2);
      min-height: var(--touch-target-comfort);
      padding: 0 var(--space-5);
      border: 2px solid transparent;
      border-radius: var(--radius-full);
      font-size: var(--text-md);
      font-weight: var(--font-weight-bold);
      cursor: pointer;
      transition: background var(--motion-fast), border-color var(--motion-fast);

      &:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
      }
    }

    .btn--primary {
      background: var(--color-primary);
      color: var(--color-text-on-primary);
      border-color: var(--color-primary);

      &:hover {
        background: var(--color-primary-hover);
        border-color: var(--color-primary-hover);
      }
    }

    .btn--secondary {
      background: var(--color-surface);
      color: var(--color-primary);
      border-color: var(--color-primary);

      &:hover {
        background: var(--color-primary-tint);
      }
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CameraCaptureComponent),
      multi: true,
    },
  ],
})
export class CameraCaptureComponent extends FieldBase<File> {
  protected readonly cameraIcon = LucideCamera;
  protected readonly userIcon = LucideUserCircle;
  protected readonly retakeIcon = LucideRefreshCw;
  protected readonly trashIcon = LucideTrash;
  protected readonly checkIcon = LucideCheck;

  @ViewChild('video') videoRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('photo') photoRef?: ElementRef<HTMLImageElement>;
  @ViewChild('reviewPhoto') reviewPhotoRef?: ElementRef<HTMLImageElement>;

  protected readonly state = signal<CaptureState>('idle');
  protected readonly previewUrl = signal<string | null>(null);
  protected readonly pendingPreviewUrl = signal<string | null>(null);
  protected readonly modalOpen = signal(false);

  protected readonly shownError = computed(() => this.errorText());

  protected readonly inlineState = computed<'empty' | 'hasPhoto'>(() =>
    this.previewUrl() ? 'hasPhoto' : 'empty',
  );

  private pendingFile: File | null = null;
  private stream: MediaStream | null = null;
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();
    this.destroyRef.onDestroy(() => {
      this.stopStream();
      this.revokePreview();
      this.revokePending();
    });
  }

  protected async startCamera(): Promise<void> {
    this.onTouched();
    this.clearPending();
    this.modalOpen.set(true);

    if (!navigator.mediaDevices?.getUserMedia) {
      this.state.set('unsupported');
      return;
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 960 } },
        audio: false,
      });
      this.state.set('streaming');

      requestAnimationFrame(() => {
        const video = this.videoRef?.nativeElement;
        if (video) {
          video.srcObject = this.stream;
          video.play().catch(() => {});
        }
      });
    } catch {
      this.state.set('denied');
    }
  }

  protected capture(): void {
    const video = this.videoRef?.nativeElement;
    if (!video) return;

    const w = video.videoWidth || 720;
    const h = video.videoHeight || 960;
    const size = Math.min(w, h);
    const sx = (w - size) / 2;
    const sy = (h - size) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);

    this.stopStream();

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
        this.setPendingPhoto(file);
      },
      'image/jpeg',
      0.85,
    );
  }

  protected retakeInModal(): void {
    this.clearPending();
    this.startCamera();
  }

  protected confirmPhoto(): void {
    if (!this.pendingFile) return;

    this.revokePreview();
    this.value.set(this.pendingFile);
    this.previewUrl.set(this.pendingPreviewUrl());
    this.onChange(this.pendingFile);

    this.pendingFile = null;
    this.pendingPreviewUrl.set(null);
    this.modalOpen.set(false);
    this.state.set('idle');
  }

  protected closeCamera(): void {
    this.stopStream();
    this.clearPending();

    if (this.value()) {
      this.state.set('idle');
    } else {
      this.state.set('idle');
    }
    this.modalOpen.set(false);
  }

  protected removePhoto(): void {
    this.revokePreview();
    this.previewUrl.set(null);
    this.value.set(null);
    this.onChange(null);
    this.state.set('idle');
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) this.setPendingPhoto(file);
    input.value = '';
  }

  override writeValue(value: File | null): void {
    super.writeValue(value);
    if (value) {
      this.revokePreview();
      this.previewUrl.set(URL.createObjectURL(value));
    } else {
      this.revokePreview();
      this.previewUrl.set(null);
    }
    this.state.set('idle');
  }

  private setPendingPhoto(file: File): void {
    this.stopStream();
    this.clearPending();
    this.pendingFile = file;
    this.pendingPreviewUrl.set(URL.createObjectURL(file));
    this.state.set('reviewing');
  }

  private clearPending(): void {
    this.revokePending();
    this.pendingFile = null;
  }

  private stopStream(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
  }

  private revokePreview(): void {
    const url = this.previewUrl();
    if (url) URL.revokeObjectURL(url);
  }

  private revokePending(): void {
    const url = this.pendingPreviewUrl();
    if (url) {
      URL.revokeObjectURL(url);
      this.pendingPreviewUrl.set(null);
    }
  }
}
